import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { Control, ControlPosition, DomUtil, Map } from 'leaflet';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { AutoSuggestionItem, AutoSuggestResult } from '@app/modules/location/models/auto-suggestion.model';
import { LocationFacade } from '@app/modules/location/facade/location.facade';
import { ResourceLoadState } from '@app/store/filters/models/resource-load.state';
import { TranslateService } from '@livemaps/i18n';
import { Translations } from '@app/core/services/i18n/translations.service';

const pois = 'pois';
const addresses = 'addresses';

@Component({
  selector: 'app-search-control',
  templateUrl: './search-control.component.html',
  styleUrls: ['./search-control.component.scss']
})
export class SearchControlComponent implements OnInit, OnDestroy {
  private _map: Map;
  searchControl: Control;
  showSearchField: boolean = false;
  pois: AutoSuggestionItem[];
  addresses: AutoSuggestionItem[];
  nonAddressResults = ['place', 'categoryQuery', 'chainQuery'];
  onDestroy$ = new Subject<void>();
  skeletonMaxCount = 3;
  searchKey: string;
  loadSuccessful: string = ResourceLoadState.LOAD_SUCCESSFUL;
  loadSuccessFail: string = ResourceLoadState.LOAD_FAILURE;
  loading: string = ResourceLoadState.LOADING;
  initial: string = ResourceLoadState.INITIAL;

  @Input() position: ControlPosition;
  @Input()
  set map(map: Map) {
    if (map) {
      this._map = map;
      const SearchControl = Control.extend({
        onAdd(map: Map) {
          return DomUtil.get('searchControl');
        },
        onRemove(map: Map) {}
      });

      this.searchControl = new SearchControl({
        position: this.position
      }).addTo(map);
    }
  }
  get map(): Map {
    return this._map;
  }

  constructor(
    private locationFacade: LocationFacade,
    public translateService: TranslateService,
    public translations: Translations
  ) {}

  ngOnInit(): void {
    this.getAutoSuggestions().subscribe((searchresult: AutoSuggestResult) => {
      this.pois = this.formatPois(searchresult.pois);
      this.addresses = searchresult.addresses;
    });
  }

  ngOnDestroy() {
    this._map?.removeControl(this.searchControl);
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  formatPois(pois: AutoSuggestionItem[]): AutoSuggestionItem[] {
    return pois?.map(item => {
      const title = item.address.label.split(',').splice(0, 3);
      return { ...item, title: title.join(',').replace(/\d+[A-z]?\s/gm, '') };
    });
  }

  loadComplete(): Observable<string> {
    return this.locationFacade.getSearchLoadState().pipe(
      map(loadState => {
        return loadState;
      }),
      takeUntil(this.onDestroy$)
    );
  }
  toggleSearchField() {
    this.showSearchField = !this.showSearchField;
    this.searchKey = '';
    this.resetResults();
  }

  onSearch(event) {
    const query = event.target.value;
    if (query.length < 5) {
      this.resetResults();
      return;
    }
    const mapCenter = this.locationFacade.getMapCenter();
    const params = {
      q: query,
      at: `${mapCenter.lat},${mapCenter.lng}`
    };
    this.locationFacade.setAutoSearchResult(params);
  }

  goToLocation(suggestionItem: AutoSuggestionItem, address = true) {
    this.searchKey = address ? suggestionItem?.address?.label : suggestionItem?.title;
    this.clearSearchResult();
    this.locationFacade.showSelectedLocation(suggestionItem.position);
  }

  getAutoSuggestions() {
    return this.locationFacade.getAutoSearchResult().pipe(
      map(response => {
        const searchResult = {};
        searchResult[pois] = response?.items.filter(item => item.resultType == 'place');
        searchResult[addresses] = response?.items.filter(item => !this.nonAddressResults.includes(item.resultType));
        return searchResult;
      }),
      takeUntil(this.onDestroy$)
    );
  }

  resetResults() {
    this.clearSearchResult();
    this.pois = [];
    this.addresses = [];
  }
  clearSearchResult() {
    this.locationFacade.clearSearchResult();
  }
}
